














































































import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import { ViewerSection, CellEditEvent } from "./DataViewerModels";
import DataViewerSectionComponent from "./DataViewerSection.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";

/**
 * Main DataViewer container component.
 * Renders multiple collapsible sections with optional edit mode and save.
 * Used by PDE viewer, LV viewer, and Beschreibung CSV viewer.
 */
@Component({
  components: {
    "data-viewer-section": DataViewerSectionComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class DataViewer extends Vue {
  @Ref("confirm") confirmationDialogComponent!: ConfirmationDialogComponent;
  /** Title displayed in the toolbar */
  @Prop({ default: "" }) title!: string;
  /** Sections to display */
  @Prop({ default: () => [] }) sections!: ViewerSection[];
  /** Whether the current user has edit permission */
  @Prop({ default: false }) canEdit!: boolean;
  /** Whether a save operation is in progress */
  @Prop({ default: false }) saving!: boolean;

  editMode = false;
  pendingEdits: CellEditEvent[] = [];
  globalSearch = "";
  debouncedSearch = "";
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  get hasChanges(): boolean {
    return this.pendingEdits.length > 0;
  }

  async toggleEditMode() {
    if (this.editMode && this.hasChanges) {
      const discard = await this.confirmationDialogComponent.show({
        title: this.$t("common.confirmationDialog.cancelTitle").toString(),
        message: this.$t("common.confirmationDialog.cancelMessage").toString(),
      });
      if (!discard) return;
      this.pendingEdits = [];
      this.$emit("discard");
    }
    this.editMode = !this.editMode;
  }

  onCellEdit(event: CellEditEvent) {
    // Replace any existing edit for the same cell
    const existingIndex = this.pendingEdits.findIndex(
      (e) => e.sectionId === event.sectionId && e.rowIndex === event.rowIndex && e.columnKey === event.columnKey
    );
    if (existingIndex >= 0) {
      this.pendingEdits.splice(existingIndex, 1, event);
    } else {
      this.pendingEdits.push(event);
    }
    this.$emit("cell-edit", event);
  }

  onSave() {
    this.$emit("save", [...this.pendingEdits]);
  }

  onCellClick(event: { sectionId: string; rowIndex: number; columnKey: string; value: string | number | null }) {
    this.$emit("cell-click", event);
  }

  /**
   * Expands a section and scrolls to a specific row, highlighting it.
   * Used for cross-reference navigation (e.g., PDF Referenzen → target row).
   */
  scrollToRow(sectionId: string, rowIndex: number) {
    const ref = this.$refs["section-" + sectionId] as DataViewerSectionComponent[] | undefined;
    if (ref && ref[0]) {
      ref[0].scrollToAndHighlightRow(rowIndex);
    }
  }

  /** Called by parent after a successful save to clear pending edits. */
  clearPendingEdits() {
    this.pendingEdits = [];
  }

  expandAll() {
    for (const section of this.sections) {
      const ref = this.$refs["section-" + section.id] as DataViewerSectionComponent[] | undefined;
      if (ref && ref[0]) {
        ref[0].isExpanded = true;
      }
    }
  }

  collapseAll() {
    for (const section of this.sections) {
      const ref = this.$refs["section-" + section.id] as DataViewerSectionComponent[] | undefined;
      if (ref && ref[0]) {
        ref[0].isExpanded = false;
      }
    }
  }

  /** When global search changes, debounce then auto-expand sections that have matching rows */
  onGlobalSearchChange() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.debouncedSearch = this.globalSearch || "";
      this.applyGlobalSearch();
    }, 300);
  }

  private applyGlobalSearch() {
    const query = (this.globalSearch || "").toLowerCase().trim();
    if (!query) return;

    for (const section of this.sections) {
      const ref = this.$refs["section-" + section.id] as DataViewerSectionComponent[] | undefined;
      if (ref && ref[0]) {
        const hasMatch = ref[0].filteredRows.length > 0;
        ref[0].isExpanded = hasMatch;
      }
    }
  }
}
